import React, { useState } from "react";
import {
  MAX_UPLOAD_FILE_MB,
  getUploadSizeError,
} from "../../../../utils/uploadLimits";
import "./ToolPrepUploadDropzone.css";

export const TOOL_PREP_UPLOAD_PRESETS = {
  image: {
    label: "Upload image",
    hint: `Supported formats: JPG, PNG, WEBP, GIF · Max ${MAX_UPLOAD_FILE_MB} MB`,
    progressLabel: "Preparing image…",
  },
  pdf: {
    label: "Upload PDF",
    hint: `Supported formats: PDF · Max ${MAX_UPLOAD_FILE_MB} MB`,
    progressLabel: "Preparing PDF…",
  },
  word: {
    label: "Upload Word file",
    hint: `Supported formats: DOC, DOCX · Max ${MAX_UPLOAD_FILE_MB} MB`,
    progressLabel: "Preparing document…",
  },
  logo: {
    label: "Upload logo",
    hint: `Supported formats: JPG, PNG, WEBP, GIF, SVG · Max ${MAX_UPLOAD_FILE_MB} MB`,
    progressLabel: "Preparing logo…",
  },
  video: {
    label: "Upload video",
    hint: `Supported formats: MP4, WEBM, MOV · Max ${MAX_UPLOAD_FILE_MB} MB`,
    progressLabel: "Preparing video…",
  },
};

const UploadIcon = () => (
  <svg
    className="tpu-dropzone__icon-svg"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 16V4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M7 9l5-5 5 5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 16.5V19a1 1 0 001 1h14a1 1 0 001-1v-2.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const Sparkle = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M6 0.5L7.2 4.2L11 5.5L7.2 6.8L6 11.5L4.8 6.8L1 5.5L4.8 4.2L6 0.5Z" />
  </svg>
);

/**
 * Shared empty-state upload module for all ToolPrep popups.
 * Pass `progress` (0–100) to show the preparing bar + sparkles.
 */
const ToolPrepUploadDropzone = ({
  kind = "image",
  label = "",
  hint = "",
  progressLabel = "",
  /** @type {number | null | undefined} */
  progress = null,
  onClick = () => {},
  onFiles = null,
  disabled = false,
  className = "",
}) => {
  const preset = TOOL_PREP_UPLOAD_PRESETS[kind] || TOOL_PREP_UPLOAD_PRESETS.image;
  const title = label || preset.label;
  const instruction = hint || preset.hint;
  const preparingLabel = progressLabel || preset.progressLabel || "Preparing…";
  const [dragOver, setDragOver] = useState(false);
  const progressActive = typeof progress === "number" && Number.isFinite(progress);
  const progressPct = progressActive
    ? Math.max(0, Math.min(100, Math.round(progress)))
    : 0;

  const handleDragOver = (e) => {
    if (disabled || progressActive) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled || progressActive) return;
    const files = e.dataTransfer?.files;
    if (files?.length && typeof onFiles === "function") {
      const first = files[0];
      const sizeError = getUploadSizeError(first);
      if (sizeError) {
        window.alert(sizeError);
        return;
      }
      onFiles(files);
      return;
    }
    onClick();
  };

  if (progressActive) {
    return (
      <div
        className={`tpu-dropzone tpu-dropzone--progress${
          className ? ` ${className}` : ""
        }`}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={`${preparingLabel} ${progressPct}%`}
      >
        <span className="tpu-dropzone__label">{preparingLabel}</span>
        <div className="tpu-dropzone__track">
          <div
            className="tpu-dropzone__bar"
            style={{ width: `${progressPct}%` }}
          >
            <span className="tpu-dropzone__sparkles" aria-hidden="true">
              <Sparkle className="tpu-dropzone__sparkle tpu-dropzone__sparkle--a" />
              <Sparkle className="tpu-dropzone__sparkle tpu-dropzone__sparkle--b" />
              <Sparkle className="tpu-dropzone__sparkle tpu-dropzone__sparkle--c" />
            </span>
          </div>
        </div>
        <span className="tpu-dropzone__hint tpu-dropzone__hint--progress">
          {progressPct}%
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`tpu-dropzone${dragOver ? " is-dragover" : ""}${
        className ? ` ${className}` : ""
      }`}
      onClick={onClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      disabled={disabled}
      aria-label={title}
    >
      <span className="tpu-dropzone__icon">
        <UploadIcon />
      </span>
      <span className="tpu-dropzone__label">{title}</span>
      <span className="tpu-dropzone__hint">{instruction}</span>
    </button>
  );
};

export default ToolPrepUploadDropzone;
